import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api.js";

export interface ConsoleUser {
  userId: string;
  email: string;
  displayName: string;
  platformRoleCode: string | null;
  mfaVerified: boolean;
}

interface AuthContextValue {
  user: ConsoleUser | null;
  loading: boolean;
  login: (
    email: string,
    password: string,
    secondFactor?: { mfaCode?: string; recoveryCode?: string },
  ) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ConsoleUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api<{ user: ConsoleUser }>("/api/auth/me")
      .then((result) => setUser(result.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async login(email, password, secondFactor) {
        const result = await api<{ user: ConsoleUser }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password, ...secondFactor }),
        });
        setUser(result.user);
      },
      async logout() {
        await api("/api/auth/logout", { method: "POST" });
        setUser(null);
      },
    }),
    [loading, user],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("AuthProvider is missing");
  return context;
}
